import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Sparkle, ArrowRight, ArrowsClockwise, CheckCircle, CaretDown, CaretRight,
  Lightning, Trophy, Target, Eye, Compass, Quotes, Calendar, Download,
  FilePdf, Heartbeat, Briefcase
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';

const API = process.env.REACT_APP_BACKEND_URL;

const PAGES = [
  { id: 'cover',     label: 'Cover',         icon: Sparkle },
  { id: 'audit',     label: 'Brand Audit',   icon: Heartbeat },
  { id: 'benchmark', label: 'Benchmark',     icon: Target },
  { id: 'strategy',  label: 'Strategy',      icon: Compass },
  { id: 'moodboard', label: 'Moodboard',     icon: Eye },
  { id: 'mockups',   label: 'Mockups',       icon: Quotes },
  { id: 'calendar',  label: 'Calendar',      icon: Calendar },
  { id: 'next',      label: 'Next Steps',    icon: Trophy },
];

const COLOR_FOR_SCORE = (n) => (n >= 70 ? '#c8f060' : n >= 45 ? '#f6c453' : '#ff9a76');

const Chip = ({ label, color = '#c8f060', children }) => (
  <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold"
    style={{ background: `${color}1f`, color, border: `1px solid ${color}33` }}>
    {label || children}
  </span>
);

const SubScoreBar = ({ label, value }) => {
  const color = COLOR_FOR_SCORE(value || 0);
  return (
    <div className="flex items-center gap-3 py-2.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
      <span className="flex-shrink-0 text-[12px]" style={{ color: 'rgba(245,240,232,0.7)', width: 140 }}>{label}</span>
      <span className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
        <span className="block h-full rounded-full" style={{ width: `${value || 0}%`, background: color }} />
      </span>
      <span className="font-bold text-sm tabular-nums" style={{ color, width: 32, textAlign: 'right' }}>{value || 0}</span>
    </div>
  );
};

const OpportunityRow = ({ surface, score, reasoning }) => {
  const color = COLOR_FOR_SCORE(score || 0);
  return (
    <div className="py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
      <div className="flex items-center gap-3">
        <span className="text-[13px] font-semibold flex-shrink-0" style={{ color: '#f5f0e8', width: 110 }}>{surface}</span>
        <span className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
          <span className="block h-full rounded-full" style={{ width: `${score || 0}%`, background: color }} />
        </span>
        <span className="font-bold text-sm tabular-nums" style={{ color, width: 32, textAlign: 'right' }}>{score || 0}</span>
      </div>
      {reasoning && <p className="text-[11px] mt-1.5 ml-[125px]" style={{ color: 'rgba(245,240,232,0.45)', lineHeight: 1.5 }}>{reasoning}</p>}
    </div>
  );
};

const MOOD_TILE_BG = [
  'linear-gradient(135deg, #c8f060, #6a8a30)',
  'linear-gradient(135deg, #1a1a1a, #2a2a2a)',
  'linear-gradient(135deg, #2c1810, #4a2b16)',
  'linear-gradient(135deg, #0f1a2e, #1e3354)',
  'linear-gradient(135deg, #2a0e2a, #4a1f4a)',
  'linear-gradient(135deg, #3a2412, #5e3a1c)',
  'rgba(255,255,255,0.04)',
  'linear-gradient(135deg, #1c2b1a, #2e4a2a)',
];

const MoodTile = ({ keyword, idx }) => (
  <div className="rounded-2xl p-5 flex items-end h-full min-h-[120px]"
    style={{
      background: MOOD_TILE_BG[idx % 8],
      color: idx === 0 ? '#0a0a0a' : 'rgba(245,240,232,0.92)',
      border: idx === 6 ? '1px solid rgba(255,255,255,0.08)' : 'none',
      fontFamily: "'Instrument Serif', serif",
      fontSize: 22,
      lineHeight: 1.18,
      fontWeight: 500,
    }}>
    {keyword}
  </div>
);

/* ════════════ DECK PAGES ════════════ */

const PageShell = ({ children, label, num }) => (
  <motion.section
    key={label}
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -12 }}
    transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
    className="relative rounded-3xl overflow-hidden"
    style={{
      background: 'linear-gradient(180deg, #0e0e0e, #080808)',
      border: '1px solid rgba(255,255,255,0.06)',
      minHeight: 620,
      boxShadow: '0 30px 80px -30px rgba(0,0,0,0.6)',
    }}
  >
    <div className="absolute top-5 left-7 right-7 flex justify-between items-center text-[10px] tracking-[0.22em] uppercase" style={{ color: 'rgba(245,240,232,0.4)' }}>
      <span className="flex items-center gap-2">
        <span className="w-3.5 h-3.5 rounded-[3px] inline-flex items-center justify-center font-extrabold text-[9px]" style={{ background: '#c8f060', color: '#0a0a0a' }}>S</span>
        ScalitOS · {label}
      </span>
      <span>{num} / 08</span>
    </div>
    <div className="px-10 pt-16 pb-12 h-full">{children}</div>
  </motion.section>
);

const CoverPage = ({ strategy, brandName, weekOf }) => {
  const conf = strategy.campaign_confidence_score || 0;
  const confColor = conf >= 85 ? '#c8f060' : conf >= 75 ? '#f6c453' : '#ff9a76';
  return (
    <PageShell label="Strategy Deck" num="01">
      <div className="flex flex-col justify-center h-full" style={{ minHeight: 520 }}>
        <p className="text-[11px] tracking-[0.22em] uppercase font-bold mb-5" style={{ color: '#c8f060' }}>Strategy deck for {brandName}</p>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight mb-5" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8', fontWeight: 500 }}>
          {strategy.campaign_name}.
        </h1>
        <p className="text-lg sm:text-xl italic max-w-2xl mb-8" style={{ color: 'rgba(245,240,232,0.7)', fontFamily: "'Instrument Serif', serif" }}>
          {strategy.campaign_tagline || strategy.emotional_angle || ''}
        </p>
        <div className="flex flex-wrap gap-2 mb-6">
          {(strategy.recommended_platforms || []).map(p => (
            <span key={p} className="px-3.5 py-1.5 rounded-full text-[12px] font-bold" style={{ background: '#c8f060', color: '#0a0a0a' }}>{p}</span>
          ))}
        </div>
        {conf > 0 && (
          <div className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-full mb-5 self-start" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${confColor}33` }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: confColor }} />
            <span className="text-[11px] font-bold tabular-nums" style={{ color: confColor }}>{conf}%</span>
            <span className="text-[10px] tracking-[0.18em] uppercase" style={{ color: 'rgba(245,240,232,0.55)' }}>strategist confidence</span>
          </div>
        )}
        <p className="text-[12px]" style={{ color: 'rgba(245,240,232,0.4)' }}>Week of {weekOf} · Prepared by your marketing team</p>
      </div>
    </PageShell>
  );
};

const AuditPage = ({ strategy }) => {
  const score = strategy.brand_health_score || 0;
  const label = strategy.brand_health_label || '—';
  const breakdown = strategy.brand_health_breakdown || {};
  const color = COLOR_FOR_SCORE(score);
  const circumference = 2 * Math.PI * 45;
  const offset = circumference * (1 - score / 100);
  return (
    <PageShell label="Brand Audit" num="02">
      <p className="text-[11px] tracking-[0.22em] uppercase font-bold mb-3" style={{ color: '#c8f060' }}>Brand Health Audit</p>
      <h2 className="text-3xl sm:text-4xl mb-8" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8', fontWeight: 500 }}>
        Where you stand today.
      </h2>
      <div className="grid lg:grid-cols-[260px,1fr] gap-8 items-start">
        <div className="text-center">
          <div className="relative w-[220px] h-[220px] mx-auto">
            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
              <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="4" />
              <circle cx="50" cy="50" r="45" fill="none" stroke={color} strokeWidth="4" strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1.4s cubic-bezier(0.16,1,0.3,1)' }} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-5xl font-bold tabular-nums" style={{ color, fontFamily: "'Instrument Serif', serif" }}>{score}</span>
              <span className="text-[10px] tracking-[0.18em] uppercase mt-1" style={{ color: 'rgba(245,240,232,0.5)' }}>{label}</span>
            </div>
          </div>
        </div>
        <div>
          <SubScoreBar label="Consistency"        value={breakdown.consistency} />
          <SubScoreBar label="Voice clarity"      value={breakdown.voice_clarity} />
          <SubScoreBar label="Distribution reach" value={breakdown.distribution_reach} />
          <SubScoreBar label="Conversion signals" value={breakdown.conversion_signals} />
          {breakdown.rationale && (
            <p className="text-xs italic mt-4 leading-relaxed" style={{ color: 'rgba(245,240,232,0.5)' }}>{breakdown.rationale}</p>
          )}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mt-8">
        <div className="p-5 rounded-2xl" style={{ background: 'rgba(200,240,96,0.04)', borderLeft: '2px solid #c8f060' }}>
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold mb-2" style={{ color: '#c8f060' }}>Key strengths</p>
          <ul className="space-y-1.5">{(strategy.key_strengths || []).map((s, i) => (
            <li key={i} className="text-sm flex gap-2" style={{ color: 'rgba(245,240,232,0.88)' }}>
              <span style={{ color: '#c8f060' }}>—</span><span>{s}</span>
            </li>))}
          </ul>
        </div>
        <div className="p-5 rounded-2xl" style={{ background: 'rgba(255,154,118,0.04)', borderLeft: '2px solid #ff9a76' }}>
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold mb-2" style={{ color: '#ff9a76' }}>Key weaknesses</p>
          <ul className="space-y-1.5">{(strategy.key_weaknesses || []).map((s, i) => (
            <li key={i} className="text-sm flex gap-2" style={{ color: 'rgba(245,240,232,0.88)' }}>
              <span style={{ color: '#ff9a76' }}>—</span><span>{s}</span>
            </li>))}
          </ul>
        </div>
      </div>
    </PageShell>
  );
};

const BenchmarkPage = ({ strategy }) => (
  <PageShell label="Industry Benchmark" num="03">
    <p className="text-[11px] tracking-[0.22em] uppercase font-bold mb-3" style={{ color: '#c8f060' }}>Industry Benchmark</p>
    <h2 className="text-3xl sm:text-4xl mb-6" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8', fontWeight: 500 }}>
      What stronger brands are doing.
    </h2>
    <div className="p-5 rounded-2xl mb-7" style={{ background: '#101010', border: '1px solid rgba(255,255,255,0.07)' }}>
      <p className="text-base leading-relaxed" style={{ color: 'rgba(245,240,232,0.85)' }}>{strategy.industry_benchmark}</p>
    </div>
    <p className="text-[11px] tracking-[0.22em] uppercase font-bold mb-2" style={{ color: 'rgba(245,240,232,0.5)' }}>Where you sit vs. the benchmark</p>
    <p className="text-base leading-relaxed mb-7" style={{ color: 'rgba(245,240,232,0.85)' }}>{strategy.where_brand_stands}</p>
    <div className="p-5 rounded-2xl" style={{ background: 'rgba(255,154,118,0.05)', borderLeft: '3px solid #ff9a76' }}>
      <p className="text-[10px] tracking-[0.2em] uppercase font-bold mb-2" style={{ color: '#ff9a76' }}>The growth gap</p>
      <p className="text-xl leading-snug" style={{ color: '#fff', fontFamily: "'Instrument Serif', serif" }}>{strategy.growth_gap}</p>
    </div>
  </PageShell>
);

const StrategyPage = ({ strategy }) => (
  <PageShell label="Strategic Recommendation" num="04">
    <p className="text-[11px] tracking-[0.22em] uppercase font-bold mb-3" style={{ color: '#c8f060' }}>This week's strategic move</p>
    <h2 className="text-3xl sm:text-4xl mb-5" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8', fontWeight: 500 }}>{strategy.campaign_name}</h2>
    <div className="pl-5 py-2 mb-7" style={{ borderLeft: '3px solid #c8f060' }}>
      <p className="text-xl sm:text-2xl italic leading-snug" style={{ color: 'rgba(245,240,232,0.95)', fontFamily: "'Instrument Serif', serif" }}>&ldquo;{strategy.core_message}&rdquo;</p>
    </div>
    <div className="grid sm:grid-cols-2 gap-4 mb-6">
      <div className="p-5 rounded-2xl" style={{ background: '#101010', border: '1px solid rgba(255,255,255,0.07)' }}>
        <p className="text-[10px] tracking-[0.2em] uppercase font-bold mb-2" style={{ color: 'rgba(245,240,232,0.5)' }}>Inside the box</p>
        <p className="text-sm leading-relaxed" style={{ color: 'rgba(245,240,232,0.82)' }}>{strategy.campaign_concept_inside_box || strategy.industry_benchmark}</p>
      </div>
      <div className="p-5 rounded-2xl" style={{ background: 'rgba(200,240,96,0.04)', border: '1px solid rgba(200,240,96,0.15)', borderLeft: '2px solid #c8f060' }}>
        <p className="text-[10px] tracking-[0.2em] uppercase font-bold mb-2" style={{ color: '#c8f060' }}>Outside the box (our pick)</p>
        <p className="text-sm leading-relaxed" style={{ color: '#fff' }}>{strategy.campaign_concept_outside_box || strategy.strategic_opportunity}</p>
      </div>
    </div>
    <div className="grid sm:grid-cols-3 gap-4">
      <div>
        <p className="text-[10px] tracking-[0.18em] uppercase font-bold mb-1.5" style={{ color: 'rgba(245,240,232,0.5)' }}>Objective</p>
        <p className="text-base font-semibold capitalize" style={{ color: '#fff' }}>{(strategy.campaign_objective || '').replace(/_/g, ' ')}</p>
      </div>
      <div>
        <p className="text-[10px] tracking-[0.18em] uppercase font-bold mb-1.5" style={{ color: 'rgba(245,240,232,0.5)' }}>Why now</p>
        <p className="text-[13px]" style={{ color: 'rgba(245,240,232,0.85)', lineHeight: 1.5 }}>{strategy.why_now}</p>
      </div>
      <div>
        <p className="text-[10px] tracking-[0.18em] uppercase font-bold mb-1.5" style={{ color: 'rgba(245,240,232,0.5)' }}>Expected wins</p>
        <ul className="text-[13px] space-y-1">{(strategy.expected_results || []).map((r, i) => (
          <li key={i} style={{ color: 'rgba(245,240,232,0.85)' }}>— {r}</li>
        ))}</ul>
      </div>
    </div>
  </PageShell>
);

const MoodboardPage = ({ strategy }) => {
  const v = strategy.visual_direction || {};
  return (
    <PageShell label="Opportunity & Visual Direction" num="05">
      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <p className="text-[11px] tracking-[0.22em] uppercase font-bold mb-3" style={{ color: '#c8f060' }}>Opportunity map</p>
          <h2 className="text-2xl mb-4" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8', fontWeight: 500 }}>Where to spend energy this week.</h2>
          <div>
            {(strategy.opportunity_scores || []).map((o) => (
              <OpportunityRow key={o.surface} surface={o.surface} score={o.score} reasoning={o.reasoning} />
            ))}
          </div>
        </div>
        <div>
          <p className="text-[11px] tracking-[0.22em] uppercase font-bold mb-3" style={{ color: '#c8f060' }}>Visual direction</p>
          <h2 className="text-2xl mb-4" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8', fontWeight: 500 }}>The mood of the campaign.</h2>
          <div className="p-5 rounded-2xl space-y-3 text-[13px]" style={{ background: '#101010', border: '1px solid rgba(255,255,255,0.07)' }}>
            {[['Mood', v.mood], ['Color direction', v.color_direction], ['Typography', v.typography], ['Photography', v.photography_style], ['References', (v.references || []).join(', ')]].map(([k, val]) => val && (
              <div key={k} className="flex" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: 6 }}>
                <span className="text-[10px] tracking-[0.18em] uppercase flex-shrink-0 w-[110px] pt-0.5" style={{ color: 'rgba(245,240,232,0.45)' }}>{k}</span>
                <span style={{ color: 'rgba(245,240,232,0.85)', lineHeight: 1.5 }}>{val}</span>
              </div>
            ))}
          </div>
          {(v.moodboard_keywords || []).length > 0 && (
            <>
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold mt-5 mb-2" style={{ color: 'rgba(245,240,232,0.5)' }}>Moodboard keywords</p>
              <div className="flex flex-wrap gap-2">
                {(v.moodboard_keywords || []).map((k, i) => (
                  <span key={i} className="px-3 py-1.5 rounded-full text-xs" style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(245,240,232,0.85)', border: '1px solid rgba(255,255,255,0.08)' }}>{k}</span>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
};

const MockupsPage = ({ strategy }) => {
  const v = strategy.visual_direction || {};
  const keywords = v.moodboard_keywords?.length ? v.moodboard_keywords : ['Editorial calm', 'Warm restraint', 'Sharp contrast', 'Quiet authority', 'Soft window light', 'Crafted detail', 'Hand-made', 'Distinctive type'];
  return (
    <PageShell label="Content Mockups" num="06">
      <p className="text-[11px] tracking-[0.22em] uppercase font-bold mb-3" style={{ color: '#c8f060' }}>Content mockups</p>
      <h2 className="text-2xl sm:text-3xl mb-6" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8', fontWeight: 500 }}>A taste of how it will land.</h2>
      <div className="grid sm:grid-cols-3 gap-3 mb-7">
        {(strategy.content_mockups || []).slice(0, 3).map((m, i) => (
          <div key={i} className="p-5 rounded-2xl" style={{ background: 'linear-gradient(180deg, #131313, #0d0d0d)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <p className="text-[9px] tracking-[0.2em] uppercase font-bold mb-2" style={{ color: '#c8f060' }}>{m.surface}</p>
            <h3 className="text-lg mb-1.5 leading-snug" style={{ fontFamily: "'Instrument Serif', serif", color: '#fff', fontWeight: 500 }}>{m.headline}</h3>
            <p className="text-[12px] mb-3" style={{ color: 'rgba(245,240,232,0.55)' }}>{m.subhead}</p>
            <p className="text-[12px] italic pl-3 py-1" style={{ color: 'rgba(245,240,232,0.85)', borderLeft: '2px solid rgba(245,240,232,0.2)', lineHeight: 1.55 }}>{m.voice_sample}</p>
          </div>
        ))}
      </div>
      <p className="text-[10px] tracking-[0.2em] uppercase font-bold mb-3" style={{ color: 'rgba(245,240,232,0.5)' }}>Moodboard</p>
      <div className="grid grid-cols-4 gap-2">
        {keywords.slice(0, 8).map((kw, i) => <MoodTile key={i} keyword={kw} idx={i} />)}
      </div>
    </PageShell>
  );
};

const CalendarPage = ({ strategy }) => (
  <PageShell label="7-Day Execution Plan" num="07">
    <p className="text-[11px] tracking-[0.22em] uppercase font-bold mb-3" style={{ color: '#c8f060' }}>7-day execution plan</p>
    <h2 className="text-2xl sm:text-3xl mb-6" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8', fontWeight: 500 }}>Your week, scheduled.</h2>
    <div className="space-y-2.5">
      {(strategy.weekly_execution_plan || []).slice(0, 7).map((d) => (
        <div key={d.day} className="grid grid-cols-[64px,1fr,1.4fr,1.2fr] gap-4 py-3.5 px-4 rounded-xl" style={{ background: '#101010', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div>
            <p className="text-[9px] tracking-[0.18em] uppercase" style={{ color: 'rgba(245,240,232,0.4)' }}>Day</p>
            <p className="text-2xl font-bold" style={{ color: '#c8f060', fontFamily: "'Instrument Serif', serif" }}>{d.day}</p>
          </div>
          <div>
            <p className="text-[13px] font-semibold" style={{ color: '#fff' }}>{d.platform}</p>
            <p className="text-[11px] capitalize" style={{ color: 'rgba(245,240,232,0.45)' }}>{d.content_type}</p>
          </div>
          <div>
            <p className="text-[13px] font-semibold leading-snug" style={{ color: '#fff' }}>{d.hook}</p>
            <p className="text-[11px] mt-1" style={{ color: 'rgba(245,240,232,0.5)', lineHeight: 1.45 }}>{d.angle}</p>
          </div>
          <div>
            <p className="text-[12px]" style={{ color: 'rgba(245,240,232,0.85)' }}>{d.cta}</p>
            <p className="text-[11px] italic mt-1" style={{ color: 'rgba(245,240,232,0.4)', lineHeight: 1.45 }}>{d.strategic_purpose}</p>
          </div>
        </div>
      ))}
    </div>
  </PageShell>
);

const NextStepsPage = ({ strategy, onLaunch, onGoToCampaign, launching, launchProgress }) => {
  const conf = strategy.campaign_confidence_score || 0;
  const confColor = conf >= 85 ? '#c8f060' : conf >= 75 ? '#f6c453' : '#ff9a76';
  const alreadyLaunched = strategy.status === 'launched' && strategy.campaign_id;
  return (
    <PageShell label="Next Steps" num="08">
      <p className="text-[11px] tracking-[0.22em] uppercase font-bold mb-3" style={{ color: '#c8f060' }}>What to do now</p>
      <h2 className="text-3xl sm:text-4xl mb-6" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8', fontWeight: 500 }}>Three moves to make this week.</h2>

      {/* Confidence badge */}
      {conf > 0 && (
        <div className="flex items-center gap-4 mb-6 p-4 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="text-center" style={{ minWidth: 78 }}>
            <p className="text-4xl font-semibold tabular-nums leading-none" style={{ fontFamily: "'Instrument Serif', serif", color: confColor }}>{conf}<span className="text-xl">%</span></p>
            <p className="text-[9px] tracking-[0.18em] uppercase mt-1" style={{ color: 'rgba(245,240,232,0.4)' }}>confidence</p>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: confColor }}>Strategist confidence</p>
            <ul className="text-[12px] space-y-0.5" style={{ color: 'rgba(245,240,232,0.75)' }}>
              {(strategy.confidence_reasons || []).slice(0, 4).map((r, i) => (
                <li key={i}>— {r}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="p-6 rounded-2xl mb-7" style={{ background: 'rgba(200,240,96,0.04)', borderLeft: '3px solid #c8f060' }}>
        <ol className="space-y-3 list-none" style={{ color: '#fff', fontFamily: "'Instrument Serif', serif" }}>
          {(strategy.next_steps && strategy.next_steps.length ? strategy.next_steps : ['Launch the campaign and let your team build the assets.', 'Review and personalise Day 1 before it publishes.', 'Track results in the campaign workspace.']).slice(0, 5).map((s, i) => (
            <li key={i} className="flex gap-3 text-lg leading-snug">
              <span className="flex-shrink-0 w-7 h-7 rounded-full inline-flex items-center justify-center text-sm font-bold mt-0.5" style={{ background: '#c8f060', color: '#0a0a0a', fontFamily: 'system-ui' }}>{i + 1}</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* LAUNCH ACTIONS */}
      {alreadyLaunched ? (
        <div className="flex flex-wrap gap-3 items-center">
          <button onClick={onGoToCampaign} data-testid="strategy-go-campaign-btn"
            className="px-6 py-3 rounded-full text-sm font-bold flex items-center gap-2 transition hover:opacity-90"
            style={{ background: '#c8f060', color: '#0a0a0a' }}>
            <Lightning size={18} weight="fill" /> Go to campaign workspace <ArrowRight size={14} />
          </button>
          <span className="text-[12px]" style={{ color: 'rgba(245,240,232,0.5)' }}>Campaign already launched · all 7 assets generated & scheduled</span>
        </div>
      ) : launching ? (
        <div data-testid="strategy-launching" className="p-5 rounded-2xl" style={{ background: 'rgba(200,240,96,0.04)', border: '1px solid rgba(200,240,96,0.15)' }}>
          <div className="flex items-center gap-3 mb-3">
            <ArrowsClockwise size={18} className="animate-spin" style={{ color: '#c8f060' }} />
            <p className="text-sm font-bold" style={{ color: '#fff' }}>
              Launching campaign — {launchProgress?.phase || 'starting'}
              {launchProgress?.current_day ? ` · day ${launchProgress.current_day}` : ''}
            </p>
            <span className="text-sm font-semibold ml-auto tabular-nums" style={{ color: '#c8f060' }}>{launchProgress?.progress || 0}%</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${launchProgress?.progress || 0}%`, background: '#c8f060' }} />
          </div>
          <p className="text-[11px] mt-3" style={{ color: 'rgba(245,240,232,0.55)' }}>Generating all 7 days of assets in your brand voice. About a minute.</p>
        </div>
      ) : (
        <div className="flex flex-wrap gap-3">
          <button onClick={onLaunch} data-testid="strategy-launch-btn"
            className="px-7 py-3.5 rounded-full text-sm font-bold flex items-center gap-2 transition hover:opacity-90"
            style={{ background: '#c8f060', color: '#0a0a0a', boxShadow: '0 8px 24px -8px rgba(200,240,96,0.4)' }}>
            <Lightning size={18} weight="fill" /> Launch campaign <ArrowRight size={14} />
          </button>
          <p className="text-[12px] self-center" style={{ color: 'rgba(245,240,232,0.5)' }}>
            We&rsquo;ll generate all 7 days of assets and drop them into your calendar.
          </p>
        </div>
      )}
    </PageShell>
  );
};

/* ════════════ MAIN PAGE ════════════ */

export default function WeeklyStrategyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [strategy, setStrategy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [activePage, setActivePage] = useState('cover');
  const [history, setHistory] = useState([]);
  const [launching, setLaunching] = useState(false);
  const [launchProgress, setLaunchProgress] = useState(null);
  const [launchJobId, setLaunchJobId] = useState(null);

  const wantedId = searchParams.get('id');

  const loadCurrent = useCallback(async () => {
    try {
      let url = `${API}/api/strategy/current`;
      if (wantedId) url = `${API}/api/strategy/${wantedId}`;
      const { data } = await axios.get(url);
      setStrategy(data.strategy || null);
    } catch (e) {
      setStrategy(null);
    } finally {
      setLoading(false);
    }
  }, [wantedId]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/strategy/history?limit=12`);
      setHistory(data.history || []);
    } catch {}
  }, []);

  useEffect(() => { loadCurrent(); loadHistory(); }, [loadCurrent, loadHistory]);

  const runStrategy = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/strategy/run`);
      setJobId(data.job_id);
      toast.success('Strategist is auditing your brand…');
    } catch (e) {
      toast.error('Could not start strategy run.');
      setGenerating(false);
    }
  };

  // Poll job status
  useEffect(() => {
    if (!jobId) return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/api/strategy/run/${jobId}`);
        if (!alive) return;
        if (data.status === 'ready' && data.strategy) {
          setStrategy(data.strategy);
          setGenerating(false);
          setJobId(null);
          setActivePage('cover');
          loadHistory();
          toast.success('Your weekly strategy is ready.');
        } else if (data.status === 'failed') {
          setGenerating(false);
          setJobId(null);
          toast.error(data.error || 'Strategist call failed.');
        } else {
          setTimeout(tick, 3500);
        }
      } catch {
        setTimeout(tick, 4500);
      }
    };
    tick();
    return () => { alive = false; };
  }, [jobId, loadHistory]);

  const downloadPdf = async () => {
    if (!strategy?.id) return;
    try {
      const resp = await axios.get(`${API}/api/strategy/${strategy.id}/deck.pdf`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(strategy.campaign_name || 'strategy-deck').toLowerCase().replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Deck downloaded.');
    } catch {
      toast.error('PDF export failed.');
    }
  };

  const approveCampaign = async () => {
    if (!strategy?.id) return;
    try {
      await axios.post(`${API}/api/strategy/${strategy.id}/status`, { status: 'approved' });
      toast.success('Campaign approved.');
      setStrategy({ ...strategy, status: 'approved' });
    } catch {
      toast.error('Could not save status.');
    }
  };

  const launchCampaign = async () => {
    if (!strategy?.id) return;
    setLaunching(true);
    setLaunchProgress({ phase: 'queued', progress: 0 });
    try {
      const { data } = await axios.post(`${API}/api/strategy/${strategy.id}/launch`);
      if (data.already_launched) {
        toast.success('Campaign already launched — taking you there.');
        navigate(`/dashboard/campaign/${data.campaign_id}`);
        return;
      }
      setLaunchJobId(data.job_id);
    } catch {
      setLaunching(false);
      setLaunchProgress(null);
      toast.error('Could not launch campaign.');
    }
  };

  const goToCampaign = () => {
    if (strategy?.campaign_id) navigate(`/dashboard/campaign/${strategy.campaign_id}`);
  };

  // Poll launch job
  useEffect(() => {
    if (!launchJobId) return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/api/strategy/launch/${launchJobId}`);
        if (!alive) return;
        setLaunchProgress(data);
        if (data.status === 'ready' && data.campaign_id) {
          setLaunching(false);
          setLaunchJobId(null);
          toast.success('Campaign launched. All 7 assets are ready.');
          navigate(`/dashboard/campaign/${data.campaign_id}`);
        } else if (data.status === 'failed') {
          setLaunching(false);
          setLaunchJobId(null);
          toast.error(data.error || 'Launch failed.');
        } else {
          setTimeout(tick, 2000);
        }
      } catch {
        setTimeout(tick, 3500);
      }
    };
    tick();
    return () => { alive = false; };
  }, [launchJobId, navigate]);

  const makeFirstContent = () => {
    const d1 = (strategy?.weekly_execution_plan || [])[0];
    if (!d1) { navigate('/dashboard/create'); return; }
    const params = new URLSearchParams({
      topic: d1.hook || strategy.core_message || '',
      platform: d1.platform || '',
      type: d1.content_type || '',
      strategyId: strategy.id || '',
    });
    navigate(`/dashboard/content-studio?${params.toString()}`);
  };

  const weekOf = useMemo(() => {
    if (!strategy?.week_of && !strategy?.created_at) return new Date().toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
    const d = new Date(strategy.week_of || strategy.created_at);
    return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
  }, [strategy]);

  const brandName = strategy?.brief_used?.match(/name: ([^\n]+)/)?.[1] || 'your brand';

  /* ── Empty / Generating states ── */
  if (loading) {
    return <DashboardLayout><div className="py-32 text-center text-sm" style={{ color: 'rgba(245,240,232,0.4)' }}>Loading…</div></DashboardLayout>;
  }

  if (generating) {
    return (
      <DashboardLayout>
        <div className="py-20 text-center" data-testid="strategy-generating">
          <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-5" style={{ background: 'rgba(200,240,96,0.06)' }}>
            <ArrowsClockwise size={28} className="animate-spin" style={{ color: '#c8f060' }} />
          </div>
          <h2 className="text-2xl mb-3" style={{ fontFamily: "'Instrument Serif', serif" }}>Your strategist is at work.</h2>
          <p className="text-sm mb-6" style={{ color: 'rgba(245,240,232,0.5)' }}>Auditing your brand, benchmarking the industry, and writing your next campaign. About a minute.</p>
          <div className="flex flex-col gap-2 max-w-sm mx-auto text-left text-[13px]" style={{ color: 'rgba(245,240,232,0.6)' }}>
            {['Gathering brand signals', 'Benchmarking against industry', 'Finding your growth gap', 'Designing this week\'s campaign', 'Writing the 7-day plan', 'Pulling sample copy in your voice'].map((s, i) => (
              <div key={i} className="flex items-center gap-2"><span style={{ color: '#c8f060' }}>—</span>{s}</div>
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!strategy) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto text-center py-16" data-testid="strategy-empty">
          <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-6" style={{ background: 'rgba(200,240,96,0.08)' }}>
            <Briefcase size={32} style={{ color: '#c8f060' }} />
          </div>
          <p className="text-[11px] tracking-[0.22em] uppercase font-bold mb-3" style={{ color: '#c8f060' }}>Weekly Strategy</p>
          <h1 className="text-4xl mb-4" style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 500 }}>
            Your marketing strategist is on staff.
          </h1>
          <p className="text-base mb-8 max-w-xl mx-auto" style={{ color: 'rgba(245,240,232,0.6)', lineHeight: 1.6 }}>
            Every Monday, ScalitOS audits your brand, benchmarks you against the industry, and proposes the single most strategic campaign to run that week — with a 7-day execution plan and an exportable deck.
          </p>
          <button onClick={runStrategy} data-testid="strategy-run-btn"
            className="px-7 py-3.5 rounded-full font-bold text-sm inline-flex items-center gap-2 transition hover:opacity-90"
            style={{ background: '#c8f060', color: '#0a0a0a' }}>
            <Sparkle size={16} weight="fill" /> Run my first strategy
          </button>
          <p className="text-xs mt-4" style={{ color: 'rgba(245,240,232,0.35)' }}>Takes about a minute. Free this week.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="strategy-page">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] tracking-[0.22em] uppercase font-bold mb-1.5" style={{ color: '#c8f060' }}>Weekly Strategy · Visual Campaign Deck</p>
            <h1 className="text-3xl sm:text-4xl" style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 500 }}>
              {strategy.campaign_name}
            </h1>
            <p className="text-sm mt-1" style={{ color: 'rgba(245,240,232,0.45)' }}>Week of {weekOf} · Objective: <span className="capitalize" style={{ color: '#c8f060' }}>{(strategy.campaign_objective || '').replace(/_/g, ' ')}</span></p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={downloadPdf} data-testid="strategy-download-pdf"
              className="px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 transition hover:opacity-90"
              style={{ background: 'rgba(255,255,255,0.06)', color: '#f5f0e8', border: '1px solid rgba(255,255,255,0.1)' }}>
              <FilePdf size={16} weight="fill" /> Download PDF
            </button>
            <button onClick={runStrategy} data-testid="strategy-rerun-btn"
              className="px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2"
              style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(245,240,232,0.7)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <ArrowsClockwise size={16} /> Re-run audit
            </button>
          </div>
        </div>

        {/* Page tabs */}
        <div className="flex gap-1 flex-wrap pb-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          {PAGES.map((p, i) => {
            const Icon = p.icon;
            const isActive = p.id === activePage;
            return (
              <button key={p.id} data-testid={`deck-tab-${p.id}`} onClick={() => setActivePage(p.id)}
                className="px-3.5 py-2 rounded-lg text-[12px] font-semibold flex items-center gap-1.5 transition"
                style={{
                  background: isActive ? 'rgba(200,240,96,0.1)' : 'transparent',
                  color: isActive ? '#c8f060' : 'rgba(245,240,232,0.5)',
                  border: isActive ? '1px solid rgba(200,240,96,0.25)' : '1px solid transparent',
                }}>
                <Icon size={13} weight={isActive ? 'fill' : 'regular'} />
                <span className="hidden sm:inline">{i + 1}. {p.label}</span>
                <span className="sm:hidden">{i + 1}</span>
              </button>
            );
          })}
        </div>

        {/* Active deck page */}
        <AnimatePresence mode="wait">
          {activePage === 'cover'     && <CoverPage key="c"   strategy={strategy} brandName={brandName} weekOf={weekOf} />}
          {activePage === 'audit'     && <AuditPage key="a"   strategy={strategy} />}
          {activePage === 'benchmark' && <BenchmarkPage key="b" strategy={strategy} />}
          {activePage === 'strategy'  && <StrategyPage key="s"  strategy={strategy} />}
          {activePage === 'moodboard' && <MoodboardPage key="m" strategy={strategy} />}
          {activePage === 'mockups'   && <MockupsPage  key="k" strategy={strategy} />}
          {activePage === 'calendar'  && <CalendarPage key="cal" strategy={strategy} />}
          {activePage === 'next'      && <NextStepsPage key="n" strategy={strategy} onLaunch={launchCampaign} onGoToCampaign={goToCampaign} launching={launching} launchProgress={launchProgress} />}
        </AnimatePresence>

        {/* Page nav */}
        <div className="flex justify-between items-center pt-2">
          <button onClick={() => {
            const idx = PAGES.findIndex(p => p.id === activePage);
            if (idx > 0) setActivePage(PAGES[idx - 1].id);
          }} disabled={activePage === 'cover'} data-testid="deck-prev-btn"
            className="px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-1.5"
            style={{ background: 'rgba(255,255,255,0.04)', color: activePage === 'cover' ? 'rgba(245,240,232,0.2)' : 'rgba(245,240,232,0.7)', cursor: activePage === 'cover' ? 'not-allowed' : 'pointer' }}>
            <CaretRight size={14} style={{ transform: 'rotate(180deg)' }} /> Previous
          </button>
          <p className="text-[11px]" style={{ color: 'rgba(245,240,232,0.35)' }}>{PAGES.findIndex(p => p.id === activePage) + 1} of {PAGES.length}</p>
          <button onClick={() => {
            const idx = PAGES.findIndex(p => p.id === activePage);
            if (idx < PAGES.length - 1) setActivePage(PAGES[idx + 1].id);
          }} disabled={activePage === 'next'} data-testid="deck-next-btn"
            className="px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-1.5"
            style={{ background: activePage === 'next' ? 'rgba(255,255,255,0.04)' : 'rgba(200,240,96,0.1)', color: activePage === 'next' ? 'rgba(245,240,232,0.3)' : '#c8f060', cursor: activePage === 'next' ? 'not-allowed' : 'pointer' }}>
            Next <CaretRight size={14} />
          </button>
        </div>

        {/* History */}
        {history.length > 1 && (
          <div className="mt-10 pt-6" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
            <p className="text-[11px] tracking-[0.22em] uppercase font-bold mb-4" style={{ color: 'rgba(245,240,232,0.4)' }}>Strategic memory</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {history.filter(h => h.id !== strategy.id).slice(0, 6).map((h) => (
                <button key={h.id} onClick={() => { window.location.search = `?id=${h.id}`; }}
                  data-testid={`history-card-${h.id}`}
                  className="text-left p-4 rounded-2xl transition hover:opacity-90"
                  style={{ background: '#101010', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <p className="text-[10px] tracking-[0.18em] uppercase mb-1.5" style={{ color: 'rgba(245,240,232,0.4)' }}>{new Date(h.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</p>
                  <p className="text-sm font-semibold mb-1.5" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif" }}>{h.campaign_name}</p>
                  <p className="text-[11px] line-clamp-2" style={{ color: 'rgba(245,240,232,0.5)' }}>{h.growth_gap}</p>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
